import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@project/contexts/AuthContext";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { toast } from "sonner";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  Upload,
  FileText,
  Calendar,
  Globe,
  Linkedin,
  Github,
  CheckCircle2,
  ArrowRight,
  File,
  X,
} from "lucide-react";
import hireflowLogo from "@project/assets/hireflow-logo.png";

export default function CandidateProfileSetup() {
  const { user, completeProfile } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    fullName: user?.name || "",
    email: user?.email || "",
    phone: "",
    dob: "",
    gender: "",
    location: "",
    currentCity: "",
    totalExperience: "",
    currentCompany: "",
    currentDesignation: "",
    highestEducation: "",
    university: "",
    graduationYear: "",
    skills: "",
    linkedinUrl: "",
    githubUrl: "",
    portfolioUrl: "",
    aboutMe: "",
  });

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleResumeSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowed.includes(file.type)) {
      toast.error("Only PDF and DOCX files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be under 5MB");
      return;
    }
    setResumeFile(file);
  };

  const handleSubmit = async () => {
    if (
      !formData.fullName.trim() ||
      !formData.email.trim() ||
      !formData.phone.trim()
    ) {
      toast.error("Please fill all required fields (Name, Email, Phone)");
      return;
    }
    if (!resumeFile) {
      toast.error("Please upload your resume");
      return;
    }

    setSubmitting(true);
    // Simulate API call
    await new Promise(r => setTimeout(r, 1500));
    completeProfile();
    setSubmitting(false);
    toast.success("Profile completed successfully! Welcome to HireFlow.");
    navigate("/dashboard");
  };

  const RequiredStar = () => <span className="ml-0.5 text-destructive">*</span>;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-border/60 bg-card/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <img
              src={hireflowLogo}
              alt="HireFlow"
              className="h-8 w-auto rounded-lg object-contain"
            />
            <div>
              <h1 className="font-display text-sm font-bold text-foreground">
                HireFlow
              </h1>
              <p className="text-[9px] text-muted-foreground">AI-Powered ATS</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
              <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
            </div>
            Step 2 of 2 — Complete Profile
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-6 pb-20">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="space-y-6"
        >
          {/* Welcome */}
          <motion.div variants={item} className="space-y-2 py-4 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
              <User className="h-7 w-7 text-primary" />
            </div>
            <h2 className="font-display text-xl font-bold text-foreground">
              Complete Your Profile
            </h2>
            <p className="mx-auto max-w-md text-sm text-muted-foreground">
              Fill in your details so recruiters can find and evaluate your
              profile. This information will be visible to hiring managers.
            </p>
          </motion.div>

          {/* Resume Upload */}
          <motion.div variants={item} className="section-card space-y-3 p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <FileText className="h-4 w-4 text-primary" /> Upload Resume{" "}
              <RequiredStar />
            </h3>
            {!resumeFile ? (
              <div
                className="cursor-pointer rounded-xl border-2 border-dashed border-border/60 p-8 text-center transition-all hover:border-primary/50 hover:bg-primary/5"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Upload className="h-5 w-5 text-primary" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  Click to upload your Resume
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  PDF or DOCX • Max 5MB
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  onChange={handleResumeSelect}
                />
              </div>
            ) : (
              <div className="flex items-center gap-4 rounded-xl border border-border/40 bg-muted/30 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <File className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {resumeFile.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {(resumeFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-destructive hover:text-destructive"
                  onClick={() => {
                    setResumeFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                >
                  <X className="mr-1 h-3.5 w-3.5" /> Remove
                </Button>
              </div>
            )}
          </motion.div>

          {/* Personal Details */}
          <motion.div variants={item} className="section-card space-y-4 p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <User className="h-4 w-4 text-primary" /> Personal Details
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-[12px] font-medium">
                  Full Name <RequiredStar />
                </Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Your full name"
                    value={formData.fullName}
                    onChange={e => updateField("fullName", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[12px] font-medium">
                  Email <RequiredStar />
                </Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={e => updateField("email", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[12px] font-medium">
                  Phone Number <RequiredStar />
                </Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="+91 9876543210"
                    value={formData.phone}
                    onChange={e => updateField("phone", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[12px] font-medium">Date of Birth</Label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="date"
                    value={formData.dob}
                    onChange={e => updateField("dob", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[12px] font-medium">Gender</Label>
                <Select
                  value={formData.gender}
                  onValueChange={v => updateField("gender", v)}
                >
                  <SelectTrigger className="mt-1 h-9 text-sm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                    <SelectItem value="prefer-not">
                      Prefer not to say
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px] font-medium">Current City</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Mumbai"
                    value={formData.currentCity}
                    onChange={e => updateField("currentCity", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Professional Details */}
          <motion.div variants={item} className="section-card space-y-4 p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Briefcase className="h-4 w-4 text-primary" /> Professional
              Details
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-[12px] font-medium">
                  Total Experience
                </Label>
                <Select
                  value={formData.totalExperience}
                  onValueChange={v => updateField("totalExperience", v)}
                >
                  <SelectTrigger className="mt-1 h-9 text-sm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fresher">Fresher</SelectItem>
                    <SelectItem value="1-2">1-2 Years</SelectItem>
                    <SelectItem value="3-5">3-5 Years</SelectItem>
                    <SelectItem value="5-8">5-8 Years</SelectItem>
                    <SelectItem value="8-12">8-12 Years</SelectItem>
                    <SelectItem value="12+">12+ Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px] font-medium">
                  Current Company
                </Label>
                <div className="relative mt-1">
                  <Briefcase className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Company name"
                    value={formData.currentCompany}
                    onChange={e =>
                      updateField("currentCompany", e.target.value)
                    }
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[12px] font-medium">
                  Current Designation
                </Label>
                <div className="relative mt-1">
                  <Briefcase className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="e.g. Software Engineer"
                    value={formData.currentDesignation}
                    onChange={e =>
                      updateField("currentDesignation", e.target.value)
                    }
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[12px] font-medium">Key Skills</Label>
                <Input
                  placeholder="e.g. React, Node.js, Python (comma separated)"
                  value={formData.skills}
                  onChange={e => updateField("skills", e.target.value)}
                  className="mt-1 h-9 text-sm"
                />
              </div>
            </div>
          </motion.div>

          {/* Education */}
          <motion.div variants={item} className="section-card space-y-4 p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <GraduationCap className="h-4 w-4 text-primary" /> Education
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-[12px] font-medium">
                  Highest Education
                </Label>
                <Select
                  value={formData.highestEducation}
                  onValueChange={v => updateField("highestEducation", v)}
                >
                  <SelectTrigger className="mt-1 h-9 text-sm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10th">10th</SelectItem>
                    <SelectItem value="12th">12th</SelectItem>
                    <SelectItem value="diploma">Diploma</SelectItem>
                    <SelectItem value="bachelors">Bachelor's Degree</SelectItem>
                    <SelectItem value="masters">Master's Degree</SelectItem>
                    <SelectItem value="phd">PhD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px] font-medium">
                  University / Institute
                </Label>
                <div className="relative mt-1">
                  <GraduationCap className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="University name"
                    value={formData.university}
                    onChange={e => updateField("university", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[12px] font-medium">
                  Graduation Year
                </Label>
                <Input
                  type="number"
                  placeholder="2024"
                  value={formData.graduationYear}
                  onChange={e => updateField("graduationYear", e.target.value)}
                  className="mt-1 h-9 text-sm"
                />
              </div>
            </div>
          </motion.div>

          {/* Links */}
          <motion.div variants={item} className="section-card space-y-4 p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Globe className="h-4 w-4 text-primary" /> Social & Portfolio
              Links
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-[12px] font-medium">
                  LinkedIn Profile
                </Label>
                <div className="relative mt-1">
                  <Linkedin className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="https://linkedin.com/in/..."
                    value={formData.linkedinUrl}
                    onChange={e => updateField("linkedinUrl", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[12px] font-medium">
                  GitHub Profile
                </Label>
                <div className="relative mt-1">
                  <Github className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="https://github.com/..."
                    value={formData.githubUrl}
                    onChange={e => updateField("githubUrl", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[12px] font-medium">
                  Portfolio Website
                </Label>
                <div className="relative mt-1">
                  <Globe className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="https://yourportfolio.com"
                    value={formData.portfolioUrl}
                    onChange={e => updateField("portfolioUrl", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* About Me */}
          <motion.div variants={item} className="section-card space-y-3 p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <User className="h-4 w-4 text-primary" /> About Me
            </h3>
            <Textarea
              placeholder="Write a brief summary about yourself, your career goals, and what you're looking for..."
              value={formData.aboutMe}
              onChange={e => updateField("aboutMe", e.target.value)}
              className="min-h-[80px] resize-none text-sm"
            />
          </motion.div>

          {/* Submit */}
          <motion.div variants={item}>
            <Button
              className="btn-glow h-11 w-full gap-2 text-sm font-semibold"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                "Saving Profile..."
              ) : (
                <>
                  Complete Profile & Continue <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
            <p className="mt-2 text-center text-[11px] text-muted-foreground">
              You can update your profile anytime from Settings
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
